import { useQuery } from '@tanstack/react-query';
import { Users, Video, CheckCircle, XCircle, Clock, HardDrive } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { adminApi } from '../api/client';
import { clsx } from 'clsx';

const COLORS = ['#0ea5e9', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6'];

export default function DashboardPage() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: () => adminApi.getStats(),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = [
    { label: '總用戶數', value: stats?.totalUsers || 0, icon: Users, color: 'bg-blue-500' },
    { label: '總任務數', value: stats?.totalJobs || 0, icon: Video, color: 'bg-purple-500' },
    { label: '已完成', value: stats?.completedJobs || 0, icon: CheckCircle, color: 'bg-green-500' },
    { label: '失敗', value: stats?.failedJobs || 0, icon: XCircle, color: 'bg-red-500' },
    { label: '進行中', value: stats?.activeJobs || 0, icon: Clock, color: 'bg-yellow-500' },
    { label: '存儲用量', value: `${((stats?.totalStorage || 0) / 1024 / 1024 / 1024).toFixed(2)} GB`, icon: HardDrive, color: 'bg-cyan-500' },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-white">儀表板</h1>
        <p className="text-gray-400 mt-1">系統概覽與數據統計</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((stat) => (
          <div key={stat.label} className="card">
            <div className={clsx('w-10 h-10 rounded-lg flex items-center justify-center mb-3', stat.color)}>
              <stat.icon className="w-5 h-5 text-white" />
            </div>
            <p className="text-2xl font-bold text-white">{stat.value}</p>
            <p className="text-sm text-gray-400">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Daily Stats */}
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">每日統計</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats?.dailyStats || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Bar dataKey="jobs" fill="#0ea5e9" name="任務數" />
                <Bar dataKey="users" fill="#22c55e" name="新用戶" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Jobs by Provider */}
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">提供商分佈</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stats?.jobsByProvider || []}
                  dataKey="count"
                  nameKey="provider"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ provider, count }) => `${provider}: ${count}`}
                >
                  {(stats?.jobsByProvider || []).map((_, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Users */}
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">最近註冊用戶</h3>
          <div className="space-y-3">
            {(stats?.recentUsers || []).slice(0, 5).map((user) => (
              <div key={user.id} className="flex items-center justify-between py-2 border-b border-gray-700 last:border-0">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center">
                    <span className="text-sm">{user.name?.[0]?.toUpperCase()}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{user.name}</p>
                    <p className="text-xs text-gray-400">{user.email}</p>
                  </div>
                </div>
                <span className={clsx(
                  'px-2 py-1 text-xs rounded-full',
                  user.tier === 'enterprise' ? 'bg-purple-500/20 text-purple-400' :
                  user.tier === 'pro' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-gray-500/20 text-gray-400'
                )}>
                  {user.tier}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Jobs */}
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">最近任務</h3>
          <div className="space-y-3">
            {(stats?.recentJobs || []).slice(0, 5).map((job) => (
              <div key={job.id} className="flex items-center justify-between py-2 border-b border-gray-700 last:border-0">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{job.prompt}</p>
                  <p className="text-xs text-gray-400">{job.provider}</p>
                </div>
                <span className={clsx(
                  'px-2 py-1 text-xs rounded-full ml-2',
                  job.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                  job.status === 'processing' ? 'bg-blue-500/20 text-blue-400' :
                  job.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                  'bg-gray-500/20 text-gray-400'
                )}>
                  {job.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
